import React, { useState } from "react";
import { formatDateForInput } from "../../../../utils/formatters";
import DatePickerInput from "../DatePickerInput";
import { US_STATES } from "../../../../data/usStatesAndCities";
import { resolveStateCode } from "../../../../utils/stateUtils";
import { getBrokerageFlags, showIdInfo } from "../../../../utils/brokerageFields";

const ID_TYPE_OPTIONS = ["DL", "Passport", "State ID"];

const ID_FIELDS = [
  { key: "type", label: "ID Type", type: "select", options: ID_TYPE_OPTIONS },
  { key: "number", label: "ID Number", type: "text" },
  { key: "issue_state", label: "Issuing State / Authority", type: "state_select" },
  { key: "issuance_date", label: "Issuance Date", type: "date" },
  { key: "expiration_date", label: "Expiration Date", type: "date" },
];

const IdDetailsForm = ({
  householdData,
  fieldData,
  missingFields,
  entityId,
  onSave,
  onSaveAndExit,
  saving,
  nextLabel,
}) => {
  const member = fieldData || {};
  const brokerageFlags = getBrokerageFlags(householdData);
  const existingIdInfo = Array.isArray(member.id_info) && member.id_info.length > 0
    ? member.id_info[0]
    : {};

  const fieldsToShow = ID_FIELDS.filter((f) => {
    const val = existingIdInfo[f.key];
    return val === null || val === undefined || val === "";
  });

  const [formData, setFormData] = useState(() => {
    const initial = {};
    fieldsToShow.forEach((f) => {
      if (f.type === "date") {
        initial[f.key] = existingIdInfo[f.key] ? formatDateForInput(existingIdInfo[f.key]) : "";
      } else {
        initial[f.key] = existingIdInfo[f.key] || "";
      }
    });
    return initial;
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const buildPayload = () => {
    const update = { ...existingIdInfo };

    fieldsToShow.forEach((f) => {
      const raw = formData[f.key];
      const val = typeof raw === "string" ? raw.trim() : raw;
      if (val !== undefined && val !== null && val !== "") {
        update[f.key] = f.key === "issue_state" ? resolveStateCode(val) : val;
      }
    });

    return {
      household_members: {
        [entityId]: {
          id_info: [update],
        },
      },
    };
  };

  const handleSubmit = (saveCallback) => {
    saveCallback(buildPayload());
  };

  if (!showIdInfo(brokerageFlags)) {
    return null;
  }

  if (fieldsToShow.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">All ID fields have been filled.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        <span className="text-sm text-red-500 font-medium">
          {fieldsToShow.length} Required field{fieldsToShow.length !== 1 ? "s" : ""} missing
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {fieldsToShow.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {f.label} <span className="text-red-500">*</span>
            </label>
            {f.type === "state_select" ? (
              <select
                value={formData[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${
                  errors[f.key] ? "border-red-400" : "border-gray-300"
                } focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
              >
                <option value="">Select...</option>
                {US_STATES.map((s) => (
                  <option key={s.abbreviation} value={s.abbreviation}>{s.name} ({s.abbreviation})</option>
                ))}
              </select>
            ) : f.type === "select" ? (
              <select
                value={formData[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${
                  errors[f.key] ? "border-red-400" : "border-gray-300"
                } focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
              >
                <option value="">Select...</option>
                {f.options.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : f.type === "date" ? (
              <DatePickerInput
                value={formData[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${
                  errors[f.key] ? "border-red-400" : "border-gray-300"
                } focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
              />
            ) : (
              <input
                type="text"
                value={formData[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg text-sm ${
                  errors[f.key] ? "border-red-400" : "border-gray-300"
                } focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500`}
              />
            )}
            {errors[f.key] && <p className="text-xs text-red-500 mt-1">{errors[f.key]}</p>}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={() => handleSubmit(onSaveAndExit)}
          disabled={saving}
          className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Save & Exit
        </button>
        <button
          onClick={() => handleSubmit(onSave)}
          disabled={saving}
          className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

export default IdDetailsForm;
